// Owned by mju-ai-workbench/shared/schemas/mirror.ts — screen mirroring over the relay's channels (D366). The AIOE session mirrors it to mju-aioe/packages/shared/src/schemas/mirror.ts; the workbench never mirrors it back. Keep the two identical.
/**
 * Watching a workbench's window, and driving it after step-up, from anywhere the relay reaches: the organisation's
 * console (Mojo Up AI Cloud Teams or Enterprise) and the phone app. One CHANNEL method on the relay's WebSocket leg
 * (D343, shared/schemas/relayChannel.ts); the mesh's `GET /mirror/stream` + `POST /mirror/input` stay for the
 * Enterprise console where a mesh exists, and the window shows the same "being watched" banner either way.
 *
 *   mirror.subscribe   CHANNEL method [ui:view; driving also needs ui:control in the signed open AND `input: true`]
 *     open    rpc { method: "mirror.subscribe", params: {@link MirrorSubscribeParams} }, `input: true` to ask for control
 *     opened  result {@link MirrorOpened}: the first frame's size, and whether this channel may drive (`control`)
 *     data    {@link MirrorData}: one JPEG frame in parts of at most {@link MIRROR_FRAME_PART_B64} base64 characters,
 *             so a `data` frame stays under the relay's 64 KB; join the parts of one `frame` in `part` order and decode
 *     input   {@link MirrorInput}: one pointer, wheel, key or text event, in frame coordinates (the last frame's size)
 *     closed  `done` when the workbench switches mirroring off, `scope` when it withdraws control from a driving
 *             channel, `revoked` under its kill switch, `refused` (with a {@link MIRROR_ERRORS} reason) at open
 *
 * The workbench serves it only while its person has `remote.mirror.enabled` on; `control` is true only while
 * `remote.mirror.allowControl` is on too, the open asked for input and the signed scopes carry `ui:control` (the
 * step-up is the platform's; the workbench checks the scope). A frame is sent only when the picture changed, at most
 * one per {@link MIRROR_CHANNEL_MIN_INTERVAL_MS}, and only once the previous frame has gone out within the client's
 * credit — so granting credit as frames are drawn is what sets the rate: a slow phone simply gets fewer frames.
 *
 * Input, in the platform's words (`pointer`, `scroll`): `pointer` is `type: "mouse"` (`action` down, up, move or
 * click), `scroll` is `type: "wheel"`, `key` is `type: "key"` (`action` down, up or char), `text` is `type: "text"`.
 * The shape is the one `POST /mirror/input` has always taken, so a console speaks one input language on both paths.
 *
 * Everything here is zod + types + constants with no other import, so a mirror is a file copy.
 */
import { z } from "zod";
export const MIRROR_CHANNEL_METHOD = "mirror.subscribe";
/** Frame width when the open names none, and the most it may ask for (larger is clamped, not refused). */
export const MIRROR_DEFAULT_WIDTH = 1280;
export const MIRROR_MAX_WIDTH = 1600;
export const MIRROR_MIN_WIDTH = 320;
/** JPEG quality: the default, and the range a request is clamped into. */
export const MIRROR_DEFAULT_QUALITY = 55;
export const MIRROR_MIN_QUALITY = 30;
export const MIRROR_MAX_QUALITY = 80;
/** The least time between two frames on one channel. */
export const MIRROR_CHANNEL_MIN_INTERVAL_MS = 250;
/** Raw JPEG bytes per part: base64 of 36 KB is exactly {@link MIRROR_FRAME_PART_B64} characters. */
export const MIRROR_FRAME_PART_BYTES = 36 * 1024;
/** The most base64 one part carries, so `{ t: "data", ch, seq, data: { … } }` stays under the relay's 64 KB frame. */
export const MIRROR_FRAME_PART_B64 = 48 * 1024;
/** The longest `text` one input event may type. */
export const MIRROR_TEXT_MAX = 1000;
/** Why an open was refused (the `closed` reason starts with one of these). */
export const MIRROR_ERRORS = {
    /** `remote.mirror.enabled` is off on this workbench. */
    off: "mirror_off",
    /** There is no window to mirror: a node, a headless host, or the window is closed right now. */
    unavailable: "mirror_unavailable",
};
export const mirrorSubscribeParamsSchema = z.object({
    /** Scale the picture down to at most this wide (default {@link MIRROR_DEFAULT_WIDTH}, clamped to {@link MIRROR_MAX_WIDTH}). */
    maxWidth: z.number().finite().positive().optional(),
    /** JPEG quality (default {@link MIRROR_DEFAULT_QUALITY}, clamped to {@link MIRROR_MIN_QUALITY}–{@link MIRROR_MAX_QUALITY}). */
    quality: z.number().finite().optional(),
});
/** The size and quality a subscription is served at, after the defaults and the clamps. */
export function normaliseMirrorParams(params) {
    const maxWidth = Math.min(MIRROR_MAX_WIDTH, Math.max(MIRROR_MIN_WIDTH, Math.round(params?.maxWidth ?? MIRROR_DEFAULT_WIDTH)));
    const quality = Math.min(MIRROR_MAX_QUALITY, Math.max(MIRROR_MIN_QUALITY, Math.round(params?.quality ?? MIRROR_DEFAULT_QUALITY)));
    return { maxWidth, quality };
}
/** `opened.result`. */
export const mirrorOpenedSchema = z.object({
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    mime: z.literal("image/jpeg"),
    /** This channel may send `input`. False for a watcher, and for an open that asked for control and was not given it. */
    control: z.boolean(),
});
/** One part of one frame. `frame` counts from 0 per channel; `part` counts from 0 to `parts - 1`. */
export const mirrorFramePartSchema = z.object({
    kind: z.literal("frame"),
    frame: z.number().int().nonnegative(),
    part: z.number().int().nonnegative(),
    parts: z.number().int().positive(),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    b64: z.string().max(MIRROR_FRAME_PART_B64),
});
/** A `data` payload on a mirror channel. One kind today; a client ignores kinds it does not know. */
export const mirrorDataSchema = z.discriminatedUnion("kind", [mirrorFramePartSchema]);
const coordinate = z.number().finite();
export const MIRROR_KEY_MODIFIERS = ["shift", "control", "alt", "meta", "cmd", "command", "ctrl", "capsLock", "numLock"];
/**
 * An `input` payload: what to replay into the window. `x` and `y` are in the coordinates of the frame the client
 * drew; the workbench maps them back onto the window. The workbench is lenient beyond this shape (an unknown button
 * is the left one, a click count is clamped to 1–3, unknown modifiers are dropped) but a client sends this.
 */
export const mirrorInputSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("mouse"),
        action: z.enum(["down", "up", "move", "click"]),
        x: coordinate,
        y: coordinate,
        button: z.enum(["left", "middle", "right"]).optional(),
        clickCount: z.number().int().min(1).max(3).optional(),
    }),
    z.object({ type: z.literal("wheel"), x: coordinate, y: coordinate, deltaX: z.number().finite().optional(), deltaY: z.number().finite().optional() }),
    z.object({ type: z.literal("key"), action: z.enum(["down", "up", "char"]), key: z.string().min(1).max(32), modifiers: z.array(z.enum(MIRROR_KEY_MODIFIERS)).optional() }),
    z.object({ type: z.literal("text"), text: z.string().min(1).max(MIRROR_TEXT_MAX) }),
]);
//# sourceMappingURL=mirror.js.map